import React from "react";
import styled from "@emotion/styled";
import { growFromBottomRounded } from "styles/animations";
import { Flex, Text } from "styles/utility-components";

import { numberFormatLong } from "components/app_layout/Typography";
import {
  AssigneeDropdownFilter,
  AssigneeFilterValue,
} from "components/opportunities/AssigneeDropdownFilter";
import OpportunityPipelineEmptyState from "components/opportunities/empty_states/OpportunityPipelineEmptyState";
import OpportunityResultsEmptyState from "components/opportunities/empty_states/OpportunityResultsEmptyState";
import { OpportunityStage } from "components/opportunities/opportunity_stage/OpportunityStage";
import { TeamDropdownFilter } from "components/opportunities/TeamDropdownFilter";
import { generateOpportunitiesSearchUrl } from "components/opportunities/utils";
import Button from "components/ui/button/Button";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import { assertCurrentUser } from "lib/currentUser";
import { useDebouncedValue } from "lib/debounce";
import { OpportunityCountSearchRequest } from "lib/generated/app-service-gql/graphql";
import { useBids } from "lib/hooks/api/bid/bids/useBids";
import { useOpportunitiesStages } from "lib/hooks/api/homepage/useOpportunitiesStages";
import { useTeams } from "lib/hooks/api/teams/useTeams";
import { useSubscription } from "lib/hooks/subscription";
import { parseBooleanValue, useURLState } from "lib/hooks/useURLState";
import { routes } from "lib/routes";
import { sysTextDefault } from "lib/themes/colors";
import { EventData, EventNames, useTracking } from "lib/tracking";
import { Box, WidgetTitle } from "./util";

type PipelineFilters = {
  teamIds?: string[];
  assignee?: AssigneeFilterValue;
};

function Pipelines() {
  const subscription = useSubscription();
  const bidModuleEnabled = subscription.hasDataTypes("BIDS");

  const { data: teams } = useTeams();

  const { data: bids } = useBids(
    {}, // Empty request fetches all bids
    {
      enabled: bidModuleEnabled,
    },
  );

  const { data: initialOpportunities, isLoading: isLoadingInitialOpportunities } =
    useOpportunitiesStages();

  const isPipelineEmpty =
    !isLoadingInitialOpportunities &&
    initialOpportunities
      ?.filter((op) => !HIDDEN_OPPORTUNITY_GROUPS.includes(op.group))
      .every((op) => (op.stage.opportunityCount ?? 0) === 0);

  const shouldShowFilters = !isLoadingInitialOpportunities && !isPipelineEmpty;

  const [filters = DEFAULT_PIPELINE_FILTERS, setFilters] = useURLState<PipelineFilters>(
    "pipelineFilters",
    DEFAULT_PIPELINE_FILTERS,
    parsePipelineUrlState,
  );

  const [debouncedFilters] = useDebouncedValue(filters, 500);

  return (
    <Flex column gap={16} flexWrap="wrap">
      <Flex justifyContent="space-between" alignItems="center">
        <Flex gap={16} alignItems="center">
          <WidgetTitle>Opportunity pipeline</WidgetTitle>
          {shouldShowFilters && (
            <Flex gap={8}>
              <AssigneeDropdownFilter
                value={filters?.assignee ? filters.assignee : { ids: [], includeNulls: false }}
                onChange={(value) => setFilters({ ...filters, assignee: value })}
              />
              {teams && teams.length > 1 && (
                <TeamDropdownFilter
                  teamIds={filters.teamIds?.length ? filters.teamIds : []}
                  onChange={(value) => setFilters({ ...filters, teamIds: value })}
                />
              )}
            </Flex>
          )}
        </Flex>
        {bids && bids.totalResults > 0 && (
          <Button
            icon={"folder"}
            buttonType="tertiary"
            blackText
            as="a"
            href={routes.bids}
            iconProps={{
              color: sysTextDefault,
            }}
          >
            View bid / no-bid reports
          </Button>
        )}
      </Flex>
      <CustomBox>
        <PipelinesContent
          debouncedFilters={debouncedFilters}
          setFilters={setFilters}
          isPipelineEmpty={isPipelineEmpty}
        />
      </CustomBox>
    </Flex>
  );
}

const HIDDEN_OPPORTUNITY_GROUPS = ["Closed", "Won"];

const eventData = {
  "Context source": "Homepage pipeline",
  "Page source": "Homepage",
} as EventData;

type PipelinesContentProps = {
  debouncedFilters: PipelineFilters;
  setFilters: (newValue: React.SetStateAction<PipelineFilters>) => void;
  isPipelineEmpty?: boolean;
};

const PipelinesContent = ({
  debouncedFilters,
  setFilters,
  isPipelineEmpty,
}: PipelinesContentProps) => {
  const { data: dataOpportunities, isLoading: isLoadingOpportunities } = useOpportunitiesStages(
    convertPipelineFiltersToSearchRequest(debouncedFilters),
  );

  const allCounts =
    dataOpportunities
      ?.filter((op) => !HIDDEN_OPPORTUNITY_GROUPS.includes(op.group))
      .map((op) => op.stage.opportunityCount ?? 0) ?? [];

  const highestCount = Math.max(...allCounts);

  if (isPipelineEmpty) {
    return (
      <OpportunityPipelineEmptyState
        eventData={{
          "Context source": "Homepage pipeline",
        }}
      />
    );
  }

  if (!isLoadingOpportunities && allCounts.every((count) => count === 0)) {
    return (
      <Flex justifyContent="center" alignItems="center">
        <OpportunityResultsEmptyState
          onClearFilters={() =>
            setFilters({
              assignee: { ids: [], includeNulls: false },
              teamIds: [],
            })
          }
        />
      </Flex>
    );
  }

  const data = isLoadingOpportunities
    ? Array.from({ length: 6 }).map((_, i) => ({
        stageId: String(i),
        order: i,
        count: 0,
        text: "",
        group: "",
        color: "",
        iconName: "",
        iconColor: "",
      }))
    : dataOpportunities
        ?.filter((transition) => !HIDDEN_OPPORTUNITY_GROUPS.includes(transition.group))
        .map((transition) => ({
          stageId: transition.stage.id,
          order: transition.order,
          count: transition.stage.opportunityCount ?? 0,
          text: transition.stage.name,
          group: transition.group,
          color: transition.stage.color,
          iconName: transition.stage.iconName,
          iconColor: transition.stage.iconColor,
        }))
        .sort((a, b) => a.order - b.order);

  return (
    <>
      {data?.map((item) => (
        <PipelineStage
          key={item.stageId}
          item={item}
          highestCount={highestCount}
          isLoading={isLoadingOpportunities}
          filters={debouncedFilters}
        />
      ))}
    </>
  );
};

const PipelineStage = ({
  item,
  highestCount,
  isLoading,
  filters,
}: {
  item: {
    stageId: string;
    count: number;
    order: number;
    text: string;
    group: string;
    color: string;
    iconName: string;
    iconColor: string;
  };
  highestCount: number;
  isLoading: boolean;
  filters: PipelineFilters;
}) => {
  const { logEvent } = useTracking();

  return (
    <StageBox
      href={
        isLoading
          ? routes.opportunities
          : generateOpportunitiesSearchUrl({
              stageIds: [item.stageId],
              assignee: filters.assignee,
              teamIds: filters.teamIds,
            })
      }
      onClick={() =>
        logEvent(EventNames.pipelineViewed, {
          ...eventData,
          "Opportunity stage group": item.group,
          "Opportunity stage name": item.text,
          "Opportunity stage index": item.order,
        })
      }
    >
      <ChartContainer>
        <Chart value={item.count} maxValue={highestCount} color={item.iconColor} />
      </ChartContainer>
      {isLoading ? (
        <SkeletonText width="50px" fontSize={17} />
      ) : (
        <CountText bold>{numberFormatLong(item.count)}</CountText>
      )}
      {isLoading ? (
        <SkeletonText width="100px" fontSize={17} />
      ) : (
        <OpportunityStage
          label={item.text}
          iconName={item.iconName}
          color={item.color}
          iconColor={item.iconColor}
        />
      )}
    </StageBox>
  );
};

const DEFAULT_PIPELINE_FILTERS: PipelineFilters = {
  assignee: {
    ids: [assertCurrentUser().guid],
    includeNulls: false,
  },
};

function parsePipelineUrlState(
  state: unknown,
  defaultFilters = DEFAULT_PIPELINE_FILTERS,
): PipelineFilters {
  if (typeof state !== "object" || state === null) {
    return defaultFilters;
  }

  const filters = state as PipelineFilters;

  return {
    ...filters,
    assignee: {
      ids: filters.assignee?.ids ?? [],
      includeNulls: parseBooleanValue(filters.assignee?.includeNulls),
    },
  };
}

function convertPipelineFiltersToSearchRequest(
  filters: PipelineFilters,
): OpportunityCountSearchRequest {
  let assignee: OpportunityCountSearchRequest["assignee"];
  if (filters.assignee && (filters.assignee.ids.length > 0 || filters.assignee.includeNulls)) {
    assignee = filters.assignee;
  }
  return { assignee, teamIds: filters.teamIds };
}

const CustomBox = styled(Box)({
  display: "grid",
  width: "100%",
  height: "100%",
  gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
  gap: "16px",
});

const StageBox = styled.a<{ qualification?: boolean }>(({ qualification, theme }) => ({
  display: "flex",
  flexDirection: "column",
  padding: "8px",
  gap: "16px",
  minWidth: "110px",
  "&:hover": {
    backgroundColor: theme.colors.sysPrimarySubtle,
    borderRadius: "4px",
  },
  ...(qualification && {
    justifyContent: "flex-end",
  }),
}));

const ChartContainer = styled.div({
  height: "140px",
  display: "flex",
  alignItems: "flex-end",
});

const Chart = styled.div<{ value: number; maxValue: number; color?: string }>(
  ({ value, maxValue, color, theme }) => ({
    height: `${(Math.min(value, maxValue) / (maxValue || 1)) * 100}%`, // Prevent division by 0 & numbers greater than 100%
    width: "100%",
    backgroundColor: color ?? theme.colors.sysPrimaryDefault,
    borderRadius: "4px",
    animation: `${growFromBottomRounded} 1s ease-in-out`,
  }),
);

const CountText = styled(Text)(() => ({
  fontSize: "17px",
}));

export default Pipelines;
